/**
 * Client-side refund routing (fallback when API refund_routing is absent).
 * Keep in sync with backend services/support_refund_routing.py.
 */

const GATEWAY_ALIASES = new Set(['GATEWAY', 'PAYMENT_GATEWAY', 'PG', 'CCAVENUE', 'ONLINE']);

export function normalizePaymentMode(booking, snap) {
  const raw = booking?.payment_mode || booking?.payment_method
    || snap?.payment_mode || snap?.payment_method;
  if (raw == null || String(raw).trim() === '') return null;
  const u = String(raw).trim().toUpperCase();
  if (u === 'WALLET' || u === 'CASH') return u === 'CASH' ? 'CASH' : 'WALLET';
  if (u === 'CREDIT' || u === 'CREDIT_LEDGER') return 'CREDIT';
  if (GATEWAY_ALIASES.has(u)) return 'GATEWAY';
  return u;
}

export function refundMethodForBooking(isAgent, paymentMode) {
  if (!isAgent) return 'offline_manual';
  if (paymentMode === 'CREDIT') return 'wallet_credit';
  if (paymentMode === 'WALLET' || paymentMode === 'CASH') return 'wallet_cash';
  return 'offline_manual';
}

function paidWithLabel(isAgent, paymentMode) {
  if (!isAgent) return 'B2C payment gateway';
  if (!paymentMode) return 'Unknown (not recorded on booking)';
  const labels = {
    CREDIT: 'Agent credit',
    WALLET: 'Agent cash wallet',
    CASH: 'Cash',
    GATEWAY: 'Payment gateway (Razorpay)',
  };
  return labels[paymentMode] || paymentMode.replace(/_/g, ' ');
}

function refundToLabel(method, paymentMode, isAgent) {
  if (method === 'wallet_cash') return 'Agent cash wallet (instant)';
  if (method === 'wallet_credit') return 'Agent credit ledger (instant)';
  if (!isAgent) return 'PG Refund Tracker (B2C gateway payout)';
  if (paymentMode === 'GATEWAY') return 'PG Refund Tracker (same gateway — accounts processes payout)';
  if (!paymentMode) return 'PG Refund Tracker (verify payment source first)';
  return 'PG Refund Tracker (queued manual / bank)';
}

function routingNote(isAgent, paymentMode, method, unknownSource) {
  if (unknownSource) {
    return 'Booking has no payment_mode — refund will queue in PG Refund Tracker. Confirm how the customer paid before processing.';
  }
  if (!isAgent) return 'B2C bookings are always refunded via the payment gateway (PG Refund Tracker).';
  if (paymentMode === 'GATEWAY' && method === 'offline_manual') {
    return 'Customer paid via payment gateway — refund is queued for accounts in PG Refund Tracker, not credited to agent wallet.';
  }
  if (method === 'wallet_cash') return 'Refund returns to the same agent cash wallet used at booking.';
  if (method === 'wallet_credit') return 'Refund restores agent credit used at booking.';
  return '';
}

/** Merge API refund_routing with client fallback from booking + snapshot. */
export function resolveRefundRouting({ apiRouting, isAgent, booking, snap }) {
  if (apiRouting?.paid_with_label) {
    return {
      ...apiRouting,
      expected_method: apiRouting.refund_method,
    };
  }
  const paymentMode = normalizePaymentMode(booking, snap);
  const expected_method = refundMethodForBooking(isAgent, paymentMode);
  const unknownSource = isAgent && !paymentMode;
  return {
    payment_mode: paymentMode,
    payment_mode_raw: booking?.payment_mode || booking?.payment_method || snap?.payment_mode,
    paid_with_label: paidWithLabel(isAgent, paymentMode),
    refund_method: expected_method,
    refund_to_label: refundToLabel(expected_method, paymentMode, isAgent),
    expected_method,
    instant: expected_method === 'wallet_cash' || expected_method === 'wallet_credit',
    queued_pg: expected_method === 'offline_manual',
    payment_source_unknown: unknownSource,
    is_gateway_paid: !isAgent || paymentMode === 'GATEWAY',
    routing_note: routingNote(isAgent, paymentMode, expected_method, unknownSource),
  };
}

export const REFUND_PAYMENT_METHOD_LABELS = {
  wallet_cash: 'Cash Wallet',
  wallet_credit: 'Credit Ledger',
  payment_gateway: 'Gateway',
  offline_manual: 'PG / Manual',
};
